// =============================================================================
// Theme Composable
// =============================================================================

import { THEME_PRESETS, DEFAULT_PRESET, COOKIE_NAME, isValidPreset } from '@/theme/presets'
import { TOKENS, type TokenName, type TokenValueMap } from '@/theme/tokens'
import type { ThemePresetId } from '@/theme/presets'

export function useTheme() {
  const presetCookie = useCookie<ThemePresetId>(COOKIE_NAME, {
    default: () => DEFAULT_PRESET,
    watch: true,
    maxAge: 60 * 60 * 24 * 365,
  })

  const currentPresetId = computed<ThemePresetId>(() =>
    isValidPreset(presetCookie.value) ? presetCookie.value : DEFAULT_PRESET
  )

  // Simple token getter - returns value for current preset
  function getToken<K extends TokenName>(tokenName: K): TokenValueMap[K] {
    return TOKENS[tokenName][currentPresetId.value]
  }

  function setPreset(presetId: ThemePresetId) {
    if (!isValidPreset(presetId)) {
      console.warn(`Invalid theme preset: ${presetId}`)
      return
    }

    presetCookie.value = presetId

    if (import.meta.client) {
      document.documentElement.setAttribute('data-preset', presetId)
    }
  }

  // Apply preset on mount (client only)
  if (import.meta.client) {
    const nuxtApp = useNuxtApp()
    nuxtApp.hook('app:mounted', () => {
      document.documentElement.setAttribute('data-preset', currentPresetId.value)
    })
  }

  return {
    currentPresetId,
    presets: THEME_PRESETS,
    getToken,
    setPreset,
  }
}
